/**
 * The ACL editor's draft (S8 Task 8.11) — pure. A draft is `{ owner, group, entries }` built from the loaded access
 * control; Save sends the FULL entries, the owner / owning group only when they changed, the loaded ETag as `ifMatch`
 * (the path's identity) and the loaded `{ owner, group, entries }` as `baseline` (compare-then-write — D11 as amended by
 * NOTES row A5: an ACL write leaves the ETag alone, so main compares the ACL itself before writing). Masks change only
 * through the mask controls (E5), so `maskChanged` is a plain comparison.
 */
import { aclEntryKey, formatPermissions, sameAclEntries } from '../../../../core/engines/azureStorageAcl';
import { orderAclEntries } from '../../../../core/engines/azureStorageAclEffective';
import type { HnsPathRef, StorageAclBaseline, StorageAclEntry, StorageAclEntryType, StorageAclScope, StorageHnsAccessControl } from '../../../../types/azureStorage';

export type AclEditorDraft = { owner: string; group: string; entries: StorageAclEntry[] };
export type AclSaveRequest = { resource: HnsPathRef; entries: StorageAclEntry[]; owner?: string; group?: string; ifMatch: string; baseline: StorageAclBaseline };

const copyEntries = (entries: readonly StorageAclEntry[]): StorageAclEntry[] => entries.map((e) => ({ ...e, permissions: { ...e.permissions } }));

export const draftFromAcl = (acl: StorageHnsAccessControl): AclEditorDraft => ({ owner: acl.owner, group: acl.group, entries: copyEntries(acl.entries) });
export const baselineOf = (acl: StorageHnsAccessControl): StorageAclBaseline => ({ owner: acl.owner, group: acl.group, entries: copyEntries(acl.entries) });

export function isDraftDirty(draft: AclEditorDraft, acl: StorageHnsAccessControl): boolean {
  return draft.owner.trim() !== acl.owner || draft.group.trim() !== acl.group || !sameAclEntries(draft.entries, acl.entries);
}

export function aclSaveRequest(acl: StorageHnsAccessControl, draft: AclEditorDraft): AclSaveRequest {
  const owner = draft.owner.trim();
  const group = draft.group.trim();
  return {
    resource: acl.resource,
    entries: orderAclEntries(draft.entries),
    ...(owner && owner !== acl.owner ? { owner } : {}),
    ...(group && group !== acl.group ? { group } : {}),
    ifMatch: acl.etag,
    baseline: baselineOf(acl),
  };
}

const maskText = (entries: readonly StorageAclEntry[]): string => entries.filter((e) => e.type === 'mask').map((e) => `${aclEntryKey(e)}|${formatPermissions(e.permissions)}`).sort().join('\n');

/** The enum-only flags of `feature:storage_acl_edit`. */
export function aclEditFlags(acl: StorageHnsAccessControl, draft: AclEditorDraft): { ownerChanged: boolean; groupChanged: boolean; maskChanged: boolean } {
  return {
    ownerChanged: !!draft.owner.trim() && draft.owner.trim() !== acl.owner,
    groupChanged: !!draft.group.trim() && draft.group.trim() !== acl.group,
    maskChanged: maskText(draft.entries) !== maskText(acl.entries),
  };
}

/** Why a named entry cannot be added: an empty or malformed object ID, or one the scope already names. */
export function namedEntryProblem(entries: readonly StorageAclEntry[], scope: StorageAclScope, type: Extract<StorageAclEntryType, 'user' | 'group'>, id: string): string | undefined {
  const value = id.trim();
  if (!value) return 'Enter the object ID (or user principal name) of the user or group.';
  if (/[:,\s]/u.test(value)) return `"${value}" is not a valid user or group id.`;
  if (entries.some((e) => e.scope === scope && e.type === type && e.id === value)) return `The ${scope} entries already name this ${type}.`;
  return undefined;
}

/**
 * A directory's first default ACL (the smallest complete default scope): the access owner and other copied, and the
 * owning group as the access MASK limits it (review opus F12; NOTES row C8 — Azure's own completion takes the owning
 * group's class from the mask) — so new children never get more than the owning group can use here now.
 */
export function withDefaultScope(entries: readonly StorageAclEntry[]): StorageAclEntry[] {
  const mask = entries.find((e) => e.scope === 'access' && e.type === 'mask')?.permissions;
  const limit = (e: StorageAclEntry) => (e.type === 'group' && mask ? { read: e.permissions.read && mask.read, write: e.permissions.write && mask.write, execute: e.permissions.execute && mask.execute } : { ...e.permissions });
  const base = entries.filter((e) => e.scope === 'access' && !e.id && e.type !== 'mask').map((e) => ({ ...e, scope: 'default' as const, permissions: limit(e) }));
  return orderAclEntries([...copyEntries(entries), ...base]);
}

export const hnsPathLabel = (ref: { fileSystem: string; path: string }): string => (ref.path ? `${ref.fileSystem}/${ref.path}` : ref.fileSystem);
